import { FilterList, SwapVert } from "@mui/icons-material";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import CircularLoader from "components/CircularLoader";
import CustomTextField from "components/Form/CustomTextField";
import { useAppContextController } from "context/AppContext";
import { useNewApplicantContext } from "context/NewApplicantContext";
import { applicantSchema } from "data/applicant";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import searchJobsWithAI from "layouts/pages/jobs/actions/searchJobsWithAI";
import ResultCard from "layouts/pages/web/components/ResultCard";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { milesToMeters } from "utils/helpers/conversion";
import OtherJobsItem from "../OtherJobsItem";

const filterValues = {
  NONE: "",
  PAY: "payRate",
  DISTANCE: "distance",
};

const sortValues = {
  PAY: "payRate",
  DISTANCE: "distance",
  RELEVANCE: "weightedScore",
};

const RecommendedJobs = () => {
  const {
    applicant,
    updateApplicantAction,
    updateButtons,
    buttonState,
    updateCurrentFormState,
    currentFormState,
  } = useNewApplicantContext();

  const { venues, company, companyType } = useAppContextController();
  const resolver = useYupValidationResolver(applicantSchema);
  const [uniqueCompanies, setUniqueCompanies] = useState({});
  const [searchFilter, setSearchFilter] = useState(null);
  const [page, setPage] = useState(1);

  const [anchorElSort, setAnchorElSort] = useState(null);
  const handleClickSort = (e) => setAnchorElSort(e.currentTarget);
  const handleCloseSort = () => setAnchorElSort(null);
  const [sortBy, setSortBy] = useState(sortValues.RELEVANCE);

  const [filterPay, setFilterPay] = useState(null);

  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [filterBy, setFilterBy] = useState(filterValues.DISTANCE);

  const [filterPayMin, setFilterPayMin] = useState("10");
  const [filterPayMax, setFilterPayMax] = useState("20");
  const [filterDistanceMax, setFilterDistanceMax] = useState("100");

  const [geoPreference, setGeoPreference] = useState("Radius");
  const [geoRadius, setGeoRadius] = useState(milesToMeters(parseInt(filterDistanceMax, 10)));

  const filterPayMinError =
    !filterPayMin || (filterPayMax && parseInt(filterPayMin, 10) > parseInt(filterPayMax, 10))
      ? "true"
      : "";
  const filterPayMaxError =
    !filterPayMax || (filterPayMin && parseInt(filterPayMin, 10) > parseInt(filterPayMax, 10))
      ? "true"
      : "";
  const filterDistanceMaxError = !filterDistanceMax ? "true" : "";

  const handleChangeFilterPayMin = (e) => {
    const val = e.target.value;
    setFilterPayMin(val.replace(/[^0-9]/gi, ""));
  };

  const handleChangeFilterPayMax = (e) => {
    const val = e.target.value;
    setFilterPayMax(val.replace(/[^0-9]/gi, ""));
  };

  const handleChangeFilterDistanceMax = (e) => {
    const val = e.target.value;
    setFilterDistanceMax(val.replace(/[^0-9]/gi, ""));
  };

  const handleSortByChange = (e) => {
    setSortBy(e.target.value);
    handleCloseSort();
  };

  const handleClickFilter = (e) => setAnchorElFilter(e.currentTarget);
  const handleCloseFilter = () => {
    if (filterBy === filterValues.DISTANCE) {
      if (filterDistanceMaxError) {
        // Error in field, set defaults and get back to None
        setFilterBy(filterValues.NONE);
        setFilterPayMin("10");
        setFilterPayMax("20");
        setFilterDistanceMax("20");
        setFilterPay(null);
        setGeoPreference("Anywhere");
        setGeoRadius(null);
      } else {
        setFilterPay(null);
        setGeoPreference("Radius");
        setGeoRadius(milesToMeters(parseInt(filterDistanceMax, 10)));
      }
    } else if (filterBy === filterValues.PAY) {
      if (filterPayMinError || filterPayMaxError) {
        // Error in field, set defaults and get back to None
        setFilterBy(filterValues.NONE);
        setFilterPayMin("10");
        setFilterPayMax("20");
        setFilterDistanceMax("20");
        setFilterPay(null);
        setGeoPreference("Anywhere");
        setGeoRadius(null);
      } else {
        setFilterPay([parseInt(filterPayMin, 10), parseInt(filterPayMax, 10)]);
        setGeoPreference("Anywhere");
        setGeoRadius(null);
      }
    } else {
      setFilterPay(null);
      setGeoPreference("Anywhere");
      setGeoRadius(null);
    }
    setAnchorElFilter(null);
  };
  const handleFilterByChange = (e) => {
    setFilterBy(e.target.value);
  };

  const options = { page, limit: 5 };
  const allOptionsFiltered = {
    // ...options,
    applicantId: applicant._id,
    filters: {},
    venues,
    useOr: true,
    orderBy: sortBy,
    geoPreference,
    ...(geoRadius ? { geoRadius } : {}),
    ...(filterPay ? { filterPayMin: filterPay[0], filterPayMax: filterPay[1] } : {}),
  };
  const searchFilterWithDefault = useMemo(() => {
    if (searchFilter && Object.keys(searchFilter).length) {
      return searchFilter;
    }
    if (company) {
      return companyType === "Venue"
        ? { venueCity: "", venueState: "" }
        : { companyState: "", companyCity: "" };
    }
    return null;
  }, [company, companyType, searchFilter]);

  const {
    data: jobsFiltered,
    isLoading: isLoadingFiltered,
    error,
  } = useQuery(
    ["jobresultsfiltered", { ...allOptionsFiltered, outsideMode: "protected" }],
    () => searchJobsWithAI({ ...allOptionsFiltered, outsideMode: "protected" }),
    {
      refetchOnWindowFocus: false,
      enabled: searchFilterWithDefault != null,
      cacheTime: 0,
    }
  );

  // const dummyJobs = [
  //   {
  //     name: "Weed Whacker",
  //     city: "Austin",
  //     state: "TX",
  //     payRate: 5,
  //   },
  //   {
  //     name: "Window Washer",
  //     city: "Austin",
  //     state: "TX",
  //     payRate: 5,
  //   },
  //   {
  //     name: "Weed Whacker",
  //     city: "Austin",
  //     state: "TX",
  //     payRate: 5,
  //   },
  // ];

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    reset,
    watch,
    trigger,
    formState: { errors, isDirty, isSubmitSuccessful, isValid },
  } = useForm({ resolver });

  const renderOtherJobs = (row, idx) => (
    <OtherJobsItem
      row={row}
      idx={idx}
      // onEdit={handleJobHistory}
      // onRemove={handleRemoveJobHistory}
    />
  );

  const sortFunction = (a, b) => {
    if (a?.companySlug) {
      if (a.companySlug > b.companySlug) return 1;
      if (a.companySlug === b.companySlug) return 0;
    }
    if (a?.venueSlug) {
      if (a.venueSlug > b.venueSlug) return 1;
      if (a.venueSlug === b.venueSlug) return 0;
    }
    return -1;
  };

  useEffect(() => {
    updateCurrentFormState({
      ...currentFormState,
      isDirty,
    });

    updateButtons({
      ...buttonState,
      submit: {
        show: true,
        disabled: !isDirty,
      },
    });
  }, [isDirty]);

  useEffect(() => {
    updateButtons({
      ...buttonState,
      previous: {
        show: true,
        disabled: false,
      },
      next: {
        show: true,
        disabled: false,
      },
      submit: {
        show: true,
        disabled: false,
      },
    });
  }, []);

  useEffect(() => {
    if (isValid) {
      updateButtons({
        ...buttonState,
        next: {
          show: true,
          disabled: false,
        },
      });
    }
  }, [isValid]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      updateButtons({
        ...buttonState,
        next: {
          show: true,
          disabled: false,
        },
        submit: {
          show: true,
          disabled: false,
        },
      });
    }
  }, [isSubmitSuccessful]);

  const canChangeDistanceFilter = companyType !== "Venue";

  return (
    <>
      <Box p={1} display="flex" justifyContent="space-between">
        <Box p={1}>
          <Typography variant="h6">Our AI has found the following recommended jobs:</Typography>
        </Box>

        <Box p={1}>
          {canChangeDistanceFilter && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<FilterList />}
              onClick={handleClickFilter}
            >
              Filter By
            </Button>
          )}
          <Menu
            anchorEl={anchorElFilter}
            open={!!anchorElFilter}
            onClose={handleCloseFilter}
            sx={{
              "& .MuiPaper-root": { minWidth: "10rem", p: "0.2rem" },
              li: { minWidth: "10rem", pl: "0.5rem", pr: "0.5rem" },
            }}
          >
            <RadioGroup
              value={filterBy}
              onChange={handleFilterByChange}
              name="radio-buttons-group"
              sx={{ p: "0.2rem" }}
            >
              <MenuItem onClick={() => {}} disableRipple sx={{ p: 0, width: "5rem" }}>
                <FormControlLabel value={filterValues.NONE} control={<Radio />} label="None" />
              </MenuItem>
              {/* TODO: Disabled for now. Need settings to show/hide Pay Rate */}
              {/* <MenuItem
                onClick={() => {}}
                disableRipple
                sx={{ p: 0, width: "5rem", display: "block" }}
              >
                <FormControlLabel value={filterValues.PAY} control={<Radio />} label="Pay Range" />
                <Grid container alignItems="center" mb={1}>
                  <CustomTextField
                    onChange={handleChangeFilterPayMin}
                    label=""
                    value={filterPayMin}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    error={!!filterPayMinError}
                    noErrorIcon
                    errorMessage={filterPayMinError}
                    inputProps={{
                      style: {
                        padding: 5,
                      },
                    }}
                    sx={{ width: "50px", marginRight: 1 }}
                  />
                  {" - "}
                  <CustomTextField
                    onChange={handleChangeFilterPayMax}
                    label=""
                    value={filterPayMax}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    error={!!filterPayMaxError}
                    noErrorIcon
                    errorMessage={filterPayMaxError}
                    inputProps={{
                      style: {
                        padding: 5,
                      },
                    }}
                    sx={{ width: "50px", marginLeft: 1 }}
                  />
                </Grid>
              </MenuItem> */}
              <MenuItem
                onClick={() => {}}
                disableRipple
                sx={{ p: 0, width: "5rem", display: "block" }}
              >
                <FormControlLabel
                  value={filterValues.DISTANCE}
                  control={<Radio />}
                  label="Distance"
                />
                <Grid container alignItems="center">
                  <CustomTextField
                    onChange={handleChangeFilterDistanceMax}
                    label="Miles"
                    value={filterDistanceMax}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    error={!!filterDistanceMaxError}
                    noErrorIcon
                    errorMessage={filterDistanceMaxError}
                    inputProps={{
                      style: {
                        padding: 5,
                      },
                    }}
                    sx={{ width: "100px", marginRight: 1, marginBottom: 1 }}
                  />
                </Grid>
              </MenuItem>
            </RadioGroup>
          </Menu>
          &nbsp;
          <Button
            variant="contained"
            color="primary"
            startIcon={<SwapVert />}
            onClick={handleClickSort}
          >
            Sort By
          </Button>
          <Menu
            anchorEl={anchorElSort}
            open={!!anchorElSort}
            onClose={handleCloseSort}
            sx={{
              "& .MuiPaper-root": { minWidth: "10rem", p: "0.2rem" },
              li: { minWidth: "10rem", pl: "0.5rem", pr: "0.5rem" },
            }}
          >
            <RadioGroup
              value={sortBy}
              onChange={handleSortByChange}
              name="radio-buttons-group"
              sx={{ p: "0.2rem" }}
            >
              {/* TODO: Disabled for now. Need settings to show/hide Pay Rate */}
              {/* <MenuItem onClick={() => {}} disableRipple sx={{ p: 0, width: "5rem" }}>
                <FormControlLabel value={sortValues.PAY} control={<Radio />} label="Pay Range" />
              </MenuItem> */}
              <MenuItem onClick={() => {}} disableRipple sx={{ p: 0, width: "5rem" }}>
                <FormControlLabel
                  value={sortValues.DISTANCE}
                  control={<Radio />}
                  label="Distance"
                />
              </MenuItem>
              <MenuItem onClick={() => {}} disableRipple sx={{ p: 0, width: "5rem" }}>
                <FormControlLabel
                  value={sortValues.RELEVANCE}
                  control={<Radio />}
                  label="Relevance Score"
                />
              </MenuItem>
            </RadioGroup>
          </Menu>
        </Box>
      </Box>
      <Box p={1} mb={2}>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "lightgray",
            borderRadius: "4px",
            overflowY: "auto",
            maxHeight: "700px",
            "& > div": {
              margin: 0,
              "& > div": {
                borderRadius: 0,
                boxShadow: "none",
              },
              "&:nth-of-type(even) > div": { border: 0, background: "rgba(0, 0, 0, 0.04)" },
              "&:last-child td, &:last-child th": { border: 0 },
            },
          }}
        >
          {jobsFiltered?.message === "Applicant do not have Resume data" && (
            <Box p={2}>
              <Typography variant="h6" color="error" textAlign="center">
                This function requires uploading a resume
              </Typography>
            </Box>
          )}
          {isLoadingFiltered && (
            <Box height="100px" position="relative">
              <CircularLoader size="2rem" color="info" hasBackdrop={false} isLoading />
            </Box>
          )}
          {!isLoadingFiltered &&
            !jobsFiltered?.data?.length &&
            jobsFiltered?.message !== "Applicant do not have Resume data" && (
              <Box p={2}>
                <Typography variant="h6" textAlign="center">
                  {canChangeDistanceFilter && geoPreference === "Radius"
                    ? "No jobs found near your location. Try increasing the max distance filter."
                    : "No jobs found"}
                </Typography>
              </Box>
            )}
          {jobsFiltered?.data?.map((result, index) => (
            <Box mt={3} key={`${index.toString() + result?.companySlug}_job_item`}>
              <ResultCard result={result} customers={uniqueCompanies} newTab />
            </Box>
          ))}
        </Box>
        {/* <DataListContainer
          // onAdd={handleJobHistory}
          noCreate
          data={dummyJobs}
          renderRow={renderOtherJobs}
          title="Other Jobs"
        /> */}
      </Box>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1 /* position: "absolute" */,
        }}
        open={isLoadingFiltered && jobsFiltered?.data?.length === 0}
      >
        <CircularProgress
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            margin: "auto",
            top: 0,
            bottom: 0,
            zIndex: 50,
          }}
        />
      </Backdrop>
    </>
  );
};

export default RecommendedJobs;
